import React from 'react';
import { IssueType, IssueVisibility } from '@momentum/types';
import { ViewGoalTitle } from './components/ViewGoalTitle';
import { ViewGoalDescription } from './components/ViewGoalDescription';
import { ViewGoalAssignees } from './components/ViewGoalAssignees';
import { ViewGoalViewers } from './components/ViewGoalViewers';
import { ViewGoalDates } from './components/ViewGoalDates';
import { ViewGoalLink } from './components/ViewGoalLink';
import { ViewGoalKeyResults } from './components/ViewGoalKeyResults';
import { ViewGoalSignals } from './components/ViewGoalSignals';
import { ViewGoalCreator } from './components/ViewGoalCreator';
import { ViewGoalPrivacy } from './components/ViewGoalPrivacy';
import { ExportToExcelButton } from './components/ExportToExcelButton';
import { StatusSelector } from '../goal-form/components/StatusSelector';
import { useAppForm } from '@/shared/components/form/form';
import {
  createGoalSchema,
  defaultValues,
} from '../goal-form/types/goals.types';
import { useUpdateTaskStatus } from '@/shared/hooks/useIssues';
import { useUpdateGoal } from '@/shared/hooks';
import { useSignalsByIds } from '@/shared/hooks/useSignals';
import {
  type FullIssueWithChildren,
  canEditIssue,
} from '@/shared/utils/issue-guards';

interface ViewGoalContentProps {
  goal: FullIssueWithChildren;
  currentEmployeeId?: string;
}

export const ViewGoalContent: React.FC<ViewGoalContentProps> = ({ goal }) => {
  const useUpdateTaskStatusMutation = useUpdateTaskStatus();
  const useUpdateGoalMutation = useUpdateGoal();
  const form = useAppForm({
    defaultValues: {
      ...defaultValues,
      status: goal.status,
    },
    validators: {
      onChange: createGoalSchema,
    },
    listeners: {
      onChange: async ({ fieldApi }) => {
        if (goal.type === IssueType.TASK) {
          await useUpdateTaskStatusMutation.mutateAsync({
            id: goal._id,
            status: fieldApi.state.value,
          });
        } else {
          await useUpdateGoalMutation.mutateAsync({
            id: goal._id,
            data: {
              status: fieldApi.state.value,
            },
          });
        }
      },
    },
  });

  // Fetch signals by IDs and merge contextual titles from relatedSignals
  const signalIds = goal.relatedSignals?.map((signal) => signal.signalId) ?? [];
  const { data: fetchedSignals, isLoading: isLoadingSignals } =
    useSignalsByIds(signalIds);

  const contextualTitleBySignalId = new Map(
    (goal.relatedSignals ?? [])
      .filter((entry) => entry.contextualTitle)
      .map((entry) => [entry.signalId, entry.contextualTitle])
  );

  const signals = fetchedSignals?.map((signal) => ({
    ...signal,
    title: contextualTitleBySignalId.get(signal._id) ?? signal.title,
  }));

  return (
    <div className="px-6 py-6 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <ViewGoalTitle title={goal.title} />
        <ExportToExcelButton goal={goal} />
      </div>
      {goal.description && (
        <ViewGoalDescription description={goal.description} />
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <ViewGoalCreator
          creator={goal.issueCreator}
          createdAt={goal.metadata.createdAt.toString()}
        />
        <ViewGoalAssignees assignees={goal.assignees} />
        <form.AppField
          name="status"
          children={(field) => (
            <StatusSelector field={field} disabled={!canEditIssue(goal)} />
          )}
        />
      </div>

      {goal.type !== IssueType.TASK && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <ViewGoalPrivacy
            visibility={goal.visibility ?? IssueVisibility.PRIVATE}
          />
        </div>
      )}

      {goal.viewers && goal.viewers.users && goal.viewers.users.length > 0 && (
        <ViewGoalViewers viewers={goal.viewers} />
      )}

      <ViewGoalDates startDate={goal.startDate} dueDate={goal.dueDate} />
      <ViewGoalLink link={goal.link} />
      <ViewGoalKeyResults
        keyResults={goal.keyResults}
        startDate={goal.startDate}
        dueDate={goal.dueDate}
      />
      <ViewGoalSignals relatedSignals={signals} isLoading={isLoadingSignals} />
    </div>
  );
};
